<!--
  Header has three internal layouts sharing one light/dark split: the internal
  content position/layout differs between the `events` page and the others.
  - seat-selection (`showLegend`): dark gradient background (fades to
    transparent toward the bottom), white text — it sits over the DVM
    map/legend.
  - events (`landing`): flat white background, dark text, with a centred
    "Welcome, {name}" + email block instead of the right-aligned
    "Hi, {name}" used elsewhere. `event-landing.component.html`'s root has no
    background of its own, so this route genuinely needs the header's
    `bg-white` — it isn't just the unstyled fallback.
  - checkout/summary (`isCartRoute`): dark text, `bg-gray-100` instead of
    `bg-white`. The header wrapper in `layout.component.html` is `fixed`
    *outside* `#main`, so a truly transparent band would expose unstyled
    `body` as a seam above the page; painting the band the same grey
    `#checkout`/`#main` already use avoids the seam. `gray-100` must match
    those routes' own `bg-gray-100` — any drift from the app's own grey
    recreates the seam. This branch must stay scoped to checkout/summary:
    it must not touch events/not-found/the okta redirect route, which keep
    `bg-white`.

  Both the "Hi, {name}"/"Welcome, {name}" text and the Sign in/Sign out pill
  reflect the same `OktaIntegrationService.getIsAuthenticated()` state — the
  checkout inline `app-okta-container` login prompt was removed in favour of
  the header's own Sign in button (see `signIn()` below). The name is never
  fabricated for a guest, and the pill just switches between Sign in/Sign out
  instead of hiding.
-->
<div
  id="header"
  class="relative flex justify-between items-center px-2 md:pl-5 md:pr-6 py-1 w-full h-full"
  [ngClass]="
    showLegend
      ? 'text-white'
      : isCartRoute
        ? 'text-gray-900 bg-gray-100'
        : 'text-gray-900 bg-white'
  ">
  <!--
    Seat-selection dark→transparent scrim, not a bar — genuinely translucent,
    floating over the DVM map. `#main` (core/layout/layout.component.html)
    starts at `top-0` on seat-selection, so the map really is behind this band.
    `pointer-events-none` so the scrim never eats clicks on the map below.
    Deliberately taller than the band itself (132px mobile / 176px
    desktop vs 48/88) so it keeps fading *past* the band and dissolves into the map instead of
    ending in a seam at the band's bottom edge — the wrapper in layout.component.html carries
    `z-10` so this overflow paints above `#main`.
  -->
  @if (showLegend) {
    <div
      aria-hidden="true"
      class="-z-10 absolute inset-x-0 top-0 h-[132px] md:h-[176px] bg-[linear-gradient(to_bottom,rgba(0,0,0,0.6),rgba(0,0,0,0))] pointer-events-none"></div>
  }

  <!-- Leading column: back button + logo kept in one flex group so `justify-between` on the
       header spaces this whole group against the right-side legend/icons instead of prying
       the back button and logo apart. -->
  <div class="flex items-center">
    <!--
      Back button: small circular outline icon button with a left-chevron,
      shown at every breakpoint (not just mobile) right before the logo.
    -->
    @if (backBtn === 'checkout') {
      <button
        data-cy="back-to-seat-selection-button"
        class="flex flex-none justify-center items-center bg-transparent hover:bg-current/10 disabled:opacity-30 mr-3 border border-current/40 rounded-full w-8 h-8 disabled:hover:transform-none hover:scale-95 transition-transform"
        (click)="goBack()">
        <svg
          class="w-3 h-3"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
          xmlns="http://www.w3.org/2000/svg">
          <path
            stroke-linecap="round"
            stroke-linejoin="round"
            stroke-width="2"
            d="M15 19l-7-7 7-7"></path>
        </svg>
      </button>
    }

    <!-- LOGO & CLUB NAME -->
    <div class="hidden md:flex items-center mr-6">
      @if (!isNotFoundRoute()) {
        <ng-container>
          <img
            [ngSrc]="configurationService.configuration.logo"
            alt="Club logo"
            class="h-12"
            width="48"
            height="48"
            [priority]="false" />
          <div class="flex items-center h-10">
            <!--
              Wordmark: both lines are 16px — "extrabold" (800) for the team
              name, "medium" (500) for "Group Sales" — the shared `h3` base
              rule (text-xl/600) and a bare `text-xs` span are both the wrong
              size/weight here. Colour is left to inherit the header's own
              `text-gray-900` / `text-white` (set on `#header` above) rather
              than a hardcoded dark navy, so it still follows the light/dark
              header split per tenant.

              `leading-4` (16px) on both lines because `text-base`/`text-sm`
              carry Tailwind's own larger default line-heights, which
              override the wrapper's `leading-tight`; the intended two-line
              block is a 16px line box each. The `gap-1` below adds
              intentional breathing room on top of that.
            -->
            <div class="flex flex-col gap-1 ml-3 min-w-max leading-tight">
              <h3 class="text-base leading-4 font-extrabold">
                {{ configurationService.configuration.clientName }}
              </h3>
              <!--
                Second line is shown on every route (not just `events`) so
                the wordmark stays consistent across the app.
              -->
              <span class="text-sm leading-4 font-medium">Group Sales</span>
            </div>
            @if (!landing && event) {
              <ng-container>
                <div class="mx-5 border-current border-l h-full"></div>
                <h5
                  [title]="event.description"
                  class="max-w-64 overflow-hidden font-bold text-sm text-nowrap">
                  <span class="font-normal">Event:&nbsp;</span
                  >{{ event.description }}
                </h5>
                <div class="mx-5 border-current border-l h-full"></div>
                <!--
                  "Tickets for" + weekday + a clock glyph + time + month/day,
                  with the clock sitting *between* the weekday and the time — one preformatted string
                  can't express that, so the getter is split into eventWeekday/eventTimeOfDay/
                  eventMonthDay (see header.component.ts). `formattedEventDate` is kept only for this
                  element's `title` tooltip.
                -->
                <h5
                  [title]="formattedEventDate"
                  class="hidden lg:flex items-center gap-x-2 mr-2 font-bold text-sm text-nowrap">
                  <span class="font-normal">Tickets for</span>
                  <span>{{ eventWeekday }}</span>
                  <!--
                    Inlined rather than an <img> so the glyph can be `currentColor`: this whole
                    `!landing && event` block is shared by seat-selection (dark header, white
                    text) AND checkout/summary (light header, `text-gray-900`), so a fixed-colour
                    asset is invisible on one of them.
                  -->
                  <svg
                    class="flex-none w-3.5 h-3.5"
                    viewBox="0 0 14 14"
                    fill="currentColor"
                    aria-hidden="true"
                    xmlns="http://www.w3.org/2000/svg">
                    <path
                      d="M6.99998 14C3.13413 14 0 10.8663 0 6.99998C0 3.13366 3.13413 0 6.99998 0C10.8658 0 14 3.13413 14 6.99998C14 10.8658 10.8658 14 6.99998 14ZM12.6 6.99998C12.6 3.9072 10.0928 1.4 6.99998 1.4C3.9072 1.4 1.4 3.9072 1.4 6.99999C1.4 10.0928 3.9072 12.6 6.99998 12.6C10.0928 12.6 12.6 10.0928 12.6 6.99999V6.99998ZM9.79998 6.29999C10.1866 6.29999 10.5 6.61339 10.5 6.99998C10.5 7.38658 10.1866 7.69998 9.79998 7.69998H7.69998C6.92999 7.69998 6.29999 7.06998 6.29999 6.29999V3.49999C6.29999 3.11339 6.61339 2.79999 6.99998 2.79999C7.38658 2.79999 7.69998 3.11339 7.69998 3.49999V6.29999H9.79998Z" />
                  </svg>
                  <span>{{ eventTimeOfDay }}.</span>
                  <span>{{ eventMonthDay }}</span>
                  <span class="font-normal text-xs">
                    (All Times {{ eventTimeZone }}. Subject to change.)
                  </span>
                </h5>
              </ng-container>
            }
          </div>
        </ng-container>
      }
    </div>
    <!-- Mobile -->
    <div class="md:hidden flex items-center mr-6">
      @if (isSectionMap || is3dOpen) {
        <div>
          <button
            class="bg-transparent disabled:opacity-30 disabled:hover:transform-none hover:scale-95 transition-transform btn-icon"
            (click)="goBackToTopview()">
            <img
              ngSrc="/assets/img/arrow-left-white-mobile-icon.svg"
              alt="Arrow left white icon"
              width="10"
              height="10" />
          </button>
        </div>
      }
      @if (!isNotFoundRoute()) {
        <ng-container>
          <img
            [ngSrc]="configurationService.configuration.logo"
            [priority]="true"
            alt="Club logo"
            width="32"
            height="32" />
          <h5 class="ml-2 min-w-max">
            {{ configurationService.configuration.clientName }}
          </h5>
        </ng-container>
      }
    </div>
  </div>

  <!--
    WELCOME BLOCK — events only. Absolutely centred against `#header` itself
    (`absolute` + `inset-x-0 mx-auto w-fit`, with `#header` `relative`), not
    via flex distribution: the groups either side are different widths on
    different tenants, so a flex-distributed "middle" child would not sit in
    the true centre. Hidden on mobile — there isn't room for a third line of
    text next to the logo.
  -->
  @if (landing && isAuthenticated) {
    <div
      class="hidden md:flex flex-col items-center absolute inset-x-0 mx-auto w-fit text-sm text-center leading-[18px]">
      <span class="font-medium">
        Welcome, <span class="font-extrabold">{{ patronName }}</span>
      </span>
      @if (patronEmail) {
        <span class="font-medium text-main">{{ patronEmail }}</span>
      }
    </div>
  }

  <!--
    Legend + info: the modal-based Legend pill shows at every breakpoint —
    the modal replaces the inline swatch legend even on wide screens.
    `isIsolatedActive` remains on the component (a one-line pass-through to
    IsolatedSeatsService) even though this was its only template usage; the
    service is intentionally left unrefactored.
  -->
  @if (showLegend && !is3dOpen) {
    <div class="flex items-center gap-x-2">
      <!--
        `bg-black/40` rather than transparent: since the header band itself
        became genuinely transparent (map visible behind it, see the scrim
        div above), this pill's thin `#707070` border alone can blend into
        bright venue imagery and become unreadable. This gives it its own
        small opaque-ish backing so it stays legible regardless of what's
        behind the header, without going fully solid like `sdc-neighbors`'
        pill (this one still reads as a lighter, secondary control).
      -->
      <button
        data-cy="open-legend-modal-button"
        (click)="openLegend()"
        class="flex justify-center items-center bg-black/40 border border-[#707070] rounded-full w-22 h-7 text-white text-xs hover:scale-95 transition-transform">
        Legend
      </button>
      @if (showTutorialButton) {
        <button
          data-cy="open-tutorial-modal-button"
          (click)="openTutorialQuestion()">
          <img
            ngSrc="/assets/img/information-icon.svg"
            alt="Information icon"
            class="disabled:opacity-30 disabled:hover:transform-none hover:scale-95 transition-transform"
            width="25"
            height="25" />
        </button>
      }
    </div>
  }

  @if (
    !showLegend &&
    (showTutorialButton ||
      showPrivateSellingActions ||
      landing ||
      (showUserInfo && isAuthenticated) ||
      (showUserInfo && !isAuthenticated))
  ) {
    <!--
      Light-header trailing group, left to right: signed-in patron name ·
      info icon · Sign in/Sign out pill (checkout/summary) — also shown on
      `events` via the `landing` disjuncts below: no "Hi, {name}" line there
      (that route gets the centred welcome block above instead), but the Sign
      in/Sign out pill itself is the same one, keyed off the same
      `isAuthenticated` state rather than a `landing`-only Okta lookup.
      `showPrivateSellingActions` already implies `landing && isAuthenticated`
      (see its getter) so it's redundant against the plain `landing` checks
      below, but is kept on the info-icon condition, which has no other
      `landing` disjunct. Kept as a single flex child so `justify-between` on
      the header spaces this whole group against the leading column, instead
      of spreading it and the icon apart as two separate items.
    -->
    <div class="flex items-center gap-x-3">
      @if (showUserInfo && isAuthenticated) {
        <span
          [title]="patronName"
          class="hidden sm:block max-w-40 text-sm text-nowrap truncate">
          Hi, {{ patronName }}
        </span>
      }
      @if (showTutorialButton || showPrivateSellingActions) {
        <button
          data-cy="open-tutorial-modal-button"
          (click)="openTutorialQuestion()">
          <img
            ngSrc="/assets/img/information-icon-navy.svg"
            alt="Information icon"
            class="disabled:opacity-30 disabled:hover:transform-none hover:scale-95 transition-transform"
            width="24"
            height="24" />
        </button>
      }
      @if ((showUserInfo || landing) && isAuthenticated) {
        <button
          data-cy="header-sign-out-button"
          class="btn-main-dark"
          (click)="signOut()">
          Sign out
        </button>
      }
      @if ((showUserInfo || landing) && !isAuthenticated) {
        <button
          data-cy="header-sign-in-button"
          class="btn-main-dark"
          (click)="signIn()">
          Sign in
        </button>
      }
    </div>
  }

  <!--
    Gift/upgrades button — stays mounted for the whole seat-selection visit
    (not `@if`-gated on is3dOpen) so it keeps fading in/out via its own
    internal state instead of being destroyed/recreated on every 3D toggle.
    Desktop-only: on mobile it moves into `app-topbar` instead, so
    the mobile header's right side is just the legend + info buttons.
    Additionally gated on the upgrades list itself so it never mounts at all
    for an event with no upgrades to show.
  -->
  @if (showLegend && upgradesService.upgrades?.length) {
    <div class="hidden md:flex items-center ml-3">
      <app-upgrades />
    </div>
  }
</div>
